import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import Button from '../common/Button';
import ReportPrintLayout from './ReportPrintLayout';
import { computeDateRange } from '../common/SectionDateFilter';
import { useGscPerformance, useGscInsights, useGscStatus } from '../../hooks/useSearchConsole';
import { useAnalyticsStatus, useWebsiteAnalytics, useAnalyticsOverview, useAnalyticsInsights } from '../../hooks/useAnalytics';
import { useFormSubmissionsCount } from '../../hooks/useFormSubmissions';
import { useBacklinksStatus } from '../../hooks/useBacklinks';
import { useKeywordsStatus } from '../../hooks/useKeywords';
import { useSeoReportStore } from '../../store/seoReportStore';

function normalizeDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildPeriodLabel(period, customFrom, customTo) {
  const from = normalizeDate(customFrom);
  const to = normalizeDate(customTo);

  if (period === 'custom' && from && to) {
    return `${from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  if (period === '7d') return 'Last 7 days';
  if (period === '28d') return 'Last 28 days';
  if (period === '2m') return 'Last 2 months';
  if (period === 'thisMonth') return 'This month';
  return 'Selected period';
}

export default function GenerateReportButton({ siteId, siteName, siteUrl, scores, strategy, history, themeKey = 'default' }) {
  const [generating, setGenerating] = useState(false);
  const [showLayout, setShowLayout] = useState(false);
  const printRef = useRef(null);
  const period = useSeoReportStore((s) => s.period);
  const customFrom = useSeoReportStore((s) => s.customFrom);
  const customTo = useSeoReportStore((s) => s.customTo);
  const normalizedCustomFrom = normalizeDate(customFrom);
  const normalizedCustomTo = normalizeDate(customTo);
  const dateRange = computeDateRange(period, normalizedCustomFrom, normalizedCustomTo);
  const periodLabel = buildPeriodLabel(period, normalizedCustomFrom, normalizedCustomTo);

  // Pre-fetch all data via hooks (they cache via React Query so no extra requests)
  const { gscStatus } = useGscStatus(siteId);
  const { performance: gscPerformance } = useGscPerformance(siteId, period, dateRange);
  const { insights: gscInsights } = useGscInsights(siteId, period, dateRange);
  const { analyticsStatus } = useAnalyticsStatus(siteId);
  const { data: websiteData } = useWebsiteAnalytics(siteId, period, dateRange);
  const { data: organicOverview } = useAnalyticsOverview(siteId, period, dateRange);
  const { insights: organicInsights } = useAnalyticsInsights(siteId, period, dateRange);
  const { status: backlinksStatus } = useBacklinksStatus(siteId);
  const { status: keywordsStatus } = useKeywordsStatus(siteId);
  const formSubmissionsEnabled = !!analyticsStatus?.formSubmissionsEnabled;
  const { count: formSubmitCount } = useFormSubmissionsCount(siteId, period, dateRange, {
    enabled: formSubmissionsEnabled,
  });

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setShowLayout(true);

    // Wait for the layout to render + recharts to paint
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const element = printRef.current;
      if (!element) throw new Error('Report layout not ready');

      const safeName = (siteName || 'site').replace(/[^a-zA-Z0-9-_]/g, '_');
      const generatedAt = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

      await html2pdf()
        .set({
          margin: [12, 10, 22, 10],
          filename: `${safeName}-seo-report.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' },
        })
        .from(element)
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          const total = pdf.internal.getNumberOfPages();
          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();
          const ruleY = pageH - 18;
          const baselineY = pageH - 11;
          for (let i = 1; i <= total; i += 1) {
            pdf.setPage(i);
            // top rule
            pdf.setDrawColor(226, 232, 240);
            pdf.setLineWidth(0.2);
            pdf.line(10, ruleY, pageW - 10, ruleY);
            // left: site + period
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(100, 116, 139);
            pdf.text(`${siteName || ''} · ${periodLabel}`, 10, baselineY, { maxWidth: 70 });
            // center: brand
            pdf.setFontSize(7.5);
            pdf.setTextColor(148, 163, 184);
            pdf.text('Report generated by', pageW / 2, baselineY - 3, { align: 'center' });
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.setTextColor(15, 23, 42);
            pdf.text('BRIGHT GROUP', pageW / 2, baselineY + 1.5, { align: 'center' });
            // right: page X of Y, date
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8.5);
            pdf.setTextColor(15, 23, 42);
            pdf.text(`Page ${i} of ${total}`, pageW - 10, baselineY - 1, { align: 'right' });
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7.5);
            pdf.setTextColor(148, 163, 184);
            pdf.text(generatedAt, pageW - 10, baselineY + 3, { align: 'right' });
          }
        })
        .save();

      toast.success('Report downloaded');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
      setShowLayout(false);
    }
  }, [siteName, siteId, scores, strategy, history, periodLabel]);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleGenerate}
        isLoading={generating}
        disabled={generating}
      >
        <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {generating ? 'Generating...' : 'Generate Report'}
      </Button>

      {/* Hidden print layout rendered off-screen for PDF capture */}
      {showLayout && createPortal(
        <div style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1 }}>
          <ReportPrintLayout
            ref={printRef}
            siteName={siteName}
            siteUrl={siteUrl}
            scores={scores}
            strategy={strategy}
            history={history}
            themeKey={themeKey}
            gscPerformance={gscPerformance}
            gscInsights={gscInsights}
            websiteData={websiteData}
            organicOverview={organicOverview}
            organicInsights={organicInsights}
            backlinks={backlinksStatus?.backlinks}
            keywords={keywordsStatus?.items}
            reportPeriodLabel={periodLabel}
            formSubmissionsEnabled={formSubmissionsEnabled}
            formSubmitCount={formSubmitCount}
          />
        </div>,
        document.body
      )}
    </>
  );
}
